import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { createClient } from "@supabase/supabase-js";

const supabaseUrl =
  process.env.REACT_APP_SUPABASE_URL || process.env.NEXT_PUBLIC_SUPABASE_URL;
const supabaseAnonKey =
  process.env.REACT_APP_SUPABASE_ANON_KEY ||
  process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    "Supabase URL and Anon Key must be set in environment variables."
  );
}
const supabase = createClient(supabaseUrl, supabaseAnonKey);

const Unsubscribe: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const handleUnsubscribe = async () => {
      try {
        const email = searchParams.get('email');
        
        if (!email) {
          setStatus('error');
          setMessage('No email address provided');
          return;
        }

        // Update user's email preferences in Supabase
        const { error } = await supabase
          .from('email_preferences')
          .upsert({
            email: email,
            welcome_emails: false,
            feature_announcements: false,
            general_notifications: false,
            updated_at: new Date().toISOString()
          });

        if (error) {
          console.error('Error updating email preferences:', error);
          setStatus('error');
          setMessage('Failed to unsubscribe. Please try again.');
        } else {
          setStatus('success');
          setMessage(`Successfully unsubscribed ${email} from all emails.`);
        }
      } catch (error) {
        console.error('Unsubscribe error:', error);
        setStatus('error');
        setMessage('An error occurred. Please try again.');
      }
    };

    handleUnsubscribe();
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-gray-100">
            {status === 'loading' && (
              <svg className="animate-spin h-6 w-6 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {status === 'success' && (
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
            {status === 'error' && (
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {status === 'loading' && 'Processing...'}
            {status === 'success' && 'Unsubscribed'}
            {status === 'error' && 'Error'}
          </h2>
        </div>
        
        <div className="text-center">
          <p className="text-sm text-gray-600">
            {message}
          </p>
          
          {status === 'success' && (
            <div className="mt-4">
              <p className="text-sm text-gray-500">
                You have been successfully unsubscribed from all emails.
              </p>
            </div>
          )}
          
          {status === 'error' && (
            <div className="mt-4">
              <p className="text-sm text-gray-500">
                If you continue to receive emails, please contact support.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Unsubscribe;
